import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    FaUserShield,
    FaLock,
    FaArrowRight,
    FaChalkboardTeacher,
    FaUserGraduate,
    FaUserCog,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import logo from "../assets/logo.jpg";
import Navbar from "../components/Navbar";
import { authService } from "../services/authService";

const LoginPage = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [userType, setUserType] = useState("admin"); // 'admin', 'teacher', 'student'
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // Client-side validation for empty fields
        if (!username.trim()) {
            setError(t("auth.usernameRequired"));
            return;
        }
        if (!password.trim()) {
            setError(t("auth.passwordRequired"));
            return;
        }

        setLoading(true);

        try {
            const result = await authService.login(
                username,
                password,
                userType,
            );
            if (result?.token) {
                const safeRole = result?.user?.role ?? null;

                if (safeRole === "teacher") {
                    navigate("/teacher");
                } else if (safeRole === "student") {
                    navigate("/student");
                } else if (safeRole === "admin") {
                    navigate("/admin");
                } else {
                    // Unknown or missing role: do not assume admin. Show error and stay on login.
                    setError(t("auth.invalidCredentials"));
                }
            } else {
                setError(result?.message || t("auth.invalidCredentials"));
            }
        } catch (error) {
            console.error("Login error:", error);
            // Extract meaningful error message from API response or use user-friendly fallback
            const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                t("auth.loginFailed");

            // Map generic HTTP status messages to user-friendly text
            if (
                errorMessage.includes("401") ||
                errorMessage.toLowerCase().includes("unauthorized")
            ) {
                setError(t("auth.invalidCredentials"));
            } else if (errorMessage.includes("400")) {
                setError(t("auth.invalidInput"));
            } else if (errorMessage.includes("500")) {
                setError(t("auth.serverError"));
            } else {
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const getUserTypeLabel = (type) => {
        switch (type) {
            case "teacher":
                return t("auth.teacher");
            case "student":
                return t("auth.student");
            default:
                return t("auth.admin");
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-safir-dark via-safir-secondary to-safir-primary">
            <Navbar />

            {/* Background decoration */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 right-10 w-72 h-72 bg-safir-primary opacity-20 rounded-full blur-3xl"></div>
                <div className="absolute bottom-10 left-10 w-96 h-96 bg-safir-secondary opacity-20 rounded-full blur-3xl"></div>
            </div>

            <div className="min-h-screen flex items-center justify-center pt-20 px-4 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
                >
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="text-center mb-8"
                    >
                        <Link to="/" className="inline-block group">
                            <motion.img
                                src={logo}
                                alt="Safir the Elite"
                                className="h-16 mx-auto mb-4 group-hover:scale-110 transition-transform"
                                whileHover={{ scale: 1.1 }}
                            />
                            <h2 className="text-3xl font-bold text-safir-dark mb-1">
                                Safir the Elite
                            </h2>
                            <p className="text-safir-primary font-semibold">
                                {getUserTypeLabel(userType)}
                            </p>
                        </Link>
                    </motion.div>

                    {/* Error Message */}
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg mb-6"
                            role="alert"
                        >
                            <p className="font-semibold">{t("common.error")}</p>
                            <p className="text-sm">{error}</p>
                        </motion.div>
                    )}

                    {/* User Type Selection */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                        className="mb-6"
                    >
                        <label className="block text-safir-dark font-semibold mb-3">
                            {t("auth.selectRole")}
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            <button
                                type="button"
                                onClick={() => setUserType("admin")}
                                className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                                    userType === "admin"
                                        ? "border-safir-primary bg-safir-primary/10 text-safir-primary"
                                        : "border-gray-200 bg-gray-50 text-gray-600 hover:border-safir-primary/50"
                                }`}
                            >
                                <FaUserCog className="mb-1" size={20} />
                                <span className="text-xs font-medium">
                                    {t("auth.admin")}
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setUserType("teacher")}
                                className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                                    userType === "teacher"
                                        ? "border-safir-primary bg-safir-primary/10 text-safir-primary"
                                        : "border-gray-200 bg-gray-50 text-gray-600 hover:border-safir-primary/50"
                                }`}
                            >
                                <FaChalkboardTeacher
                                    className="mb-1"
                                    size={20}
                                />
                                <span className="text-xs font-medium">
                                    {t("auth.teacher")}
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setUserType("student")}
                                className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                                    userType === "student"
                                        ? "border-safir-primary bg-safir-primary/10 text-safir-primary"
                                        : "border-gray-200 bg-gray-50 text-gray-600 hover:border-safir-primary/50"
                                }`}
                            >
                                <FaUserGraduate className="mb-1" size={20} />
                                <span className="text-xs font-medium">
                                    {t("auth.student")}
                                </span>
                            </button>
                        </div>
                    </motion.div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Username Field */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <label
                                htmlFor="username"
                                className="block text-safir-dark font-semibold mb-2"
                            >
                                {t("auth.username")}
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <FaUserShield
                                        className="text-safir-primary group-focus-within:text-safir-secondary transition-colors"
                                        size={18}
                                    />
                                </div>
                                <input
                                    type="text"
                                    id="username"
                                    className="pl-12 w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-safir-primary focus:outline-none transition-colors bg-gray-50 focus:bg-white"
                                    placeholder={
                                        userType === "admin"
                                            ? t("auth.adminPlaceholder")
                                            : userType === "teacher"
                                              ? t("auth.teacherPlaceholder")
                                              : t("auth.studentPlaceholder")
                                    }
                                    value={username}
                                    onChange={(e) =>
                                        setUsername(e.target.value)
                                    }
                                    required
                                />
                            </div>
                        </motion.div>

                        {/* Password Field */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <label
                                htmlFor="password"
                                className="block text-safir-dark font-semibold mb-2"
                            >
                                {t("auth.password")}
                            </label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <FaLock
                                        className="text-safir-primary group-focus-within:text-safir-secondary transition-colors"
                                        size={18}
                                    />
                                </div>
                                <input
                                    type="password"
                                    id="password"
                                    className="pl-12 w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-safir-primary focus:outline-none transition-colors bg-gray-50 focus:bg-white"
                                    placeholder={t("auth.passwordPlaceholder")}
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(e.target.value)
                                    }
                                    required
                                />
                            </div>
                        </motion.div>

                        {/* Submit Button */}
                        <motion.button
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            className={`w-full bg-gradient-to-r from-safir-primary to-safir-secondary hover:shadow-lg text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 ${
                                loading ? "opacity-70 cursor-not-allowed" : ""
                            }`}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                                    {t("common.loading")}...
                                </>
                            ) : (
                                <>
                                    {t("auth.loginButton")}
                                    <FaArrowRight size={16} />
                                </>
                            )}
                        </motion.button>
                    </form>

                    {/* Language Switcher */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="mt-6 text-center"
                    ></motion.div>

                    {/* Back to Website */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.55 }}
                        className="mt-4 text-center"
                    >
                        <Link
                            to="/"
                            className="text-safir-primary hover:text-safir-secondary font-semibold transition-colors inline-flex items-center gap-2"
                        >
                            ← {t("common.home")}
                        </Link>
                    </motion.div>

                    {/* Sign Up / Access Guidance */}
                    {(userType === "teacher" || userType === "student") && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.55 }}
                            className="mt-4 text-center"
                        >
                            <p className="text-sm text-gray-600">
                                {t("auth.dontHaveAccount")}{" "}
                                {t("auth.contactAdmin")}
                            </p>
                        </motion.div>
                    )}

                    {/* Help Section */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className="mt-8 pt-6 border-t-2 border-gray-100"
                    >
                        <p className="text-xs text-gray-500 text-center font-semibold mb-2">
                            {t("auth.help")}
                        </p>
                        <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-center text-xs text-gray-600">
                            <p>
                                <span className="font-semibold">
                                    {t("auth.contactUs")}:
                                </span>{" "}
                                +93 (78) 281 883{" "}
                            </p>
                            <p className="text-gray-500">
                                {userType === "admin" &&
                                    t("auth.adminAccountInfo")}
                                {userType === "teacher" &&
                                    t("auth.teacherAccountInfo")}
                                {userType === "student" &&
                                    t("auth.studentAccountInfo")}
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default LoginPage;
